import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import PermissionGate from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GraduationCap, Plus, Pencil, Trash2, Search } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", branch_id: "", expertise: [], bio: "", status: "Active" };

export default function Trainers() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [expertiseText, setExpertiseText] = useState("");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/trainers", { params: { search: search || undefined } });
    setRows(data.items || []);
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/branches").then(({ data }) => setBranches(data || [])); }, []);

  const openNew = () => { setForm(EMPTY); setExpertiseText(""); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...EMPTY, ...t }); setExpertiseText((t.expertise || []).join(", ")); setEditId(t.id); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, expertise: expertiseText.split(",").map((s) => s.trim()).filter(Boolean) };
      if (editId) await api.put(`/trainers/${editId}`, payload);
      else await api.post("/trainers", payload);
      toast.success(editId ? "Trainer updated" : "Trainer created");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete trainer "${t.name}"?`)) return;
    try { await api.delete(`/trainers/${t.id}`); toast.success("Trainer deleted"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="trainers-page">
      <PageHeader title="Trainers" subtitle="Manage your training faculty." />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search trainers..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-64 pl-9" data-testid="trainer-search" />
        </div>
        <PermissionGate permission="trainer.create">
          <Button onClick={openNew} data-testid="add-trainer-button"><Plus className="mr-1.5 h-4 w-4" /> Add trainer</Button>
        </PermissionGate>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Contact</TableHead>
              <TableHead>Expertise</TableHead><TableHead>Branch</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No trainers yet.</TableCell></TableRow>}
            {rows.map((t) => (
              <TableRow key={t.id} data-testid={`trainer-row-${t.trainer_code}`}>
                <TableCell className="font-medium">{t.trainer_code}</TableCell>
                <TableCell className="flex items-center gap-2 font-medium text-secondary"><GraduationCap className="h-4 w-4 text-primary" />{t.name}</TableCell>
                <TableCell className="text-sm">{t.email || "—"}<br /><span className="text-muted-foreground">{t.phone}</span></TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{(t.expertise || []).slice(0, 3).map((x) => <Badge key={x} className="border-0 bg-primary/10 text-[11px] text-primary">{x}</Badge>)}</div></TableCell>
                <TableCell>{t.branch_name || "—"}</TableCell>
                <TableCell><Badge className={t.status === "Active" ? "border-0 bg-accent/10 text-accent" : "border-0 bg-slate-100 text-slate-600"}>{t.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <PermissionGate permission="trainer.update"><Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-trainer-${t.trainer_code}`}><Pencil className="h-4 w-4" /></Button></PermissionGate>
                  <PermissionGate permission="trainer.delete"><Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(t)} data-testid={`delete-trainer-${t.trainer_code}`}><Trash2 className="h-4 w-4" /></Button></PermissionGate>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="trainer-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit trainer" : "Add trainer"}</DialogTitle>
            <DialogDescription>Capture the trainer's contact, expertise and branch.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="trainer-name-input" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={set("email")} data-testid="trainer-email-input" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="trainer-phone-input" /></div>
            <div>
              <Label>Branch</Label>
              <Select value={form.branch_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, branch_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="trainer-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="trainer-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Expertise (comma separated)</Label><Input value={expertiseText} onChange={(e) => setExpertiseText(e.target.value)} placeholder="Salesforce, Apex, LWC" data-testid="trainer-expertise-input" /></div>
            <div className="col-span-2"><Label>Bio</Label><Textarea value={form.bio} onChange={set("bio")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-trainer-button">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
